
































import Vue from 'vue';
import Component, { mixins } from 'vue-class-component';
import { CookieBanner } from '@/common/ts/cookie-banner/CookieBanner';
import VideoPauseHandler from '@/common/ts/pausing-videos/VideoPauseHandler';
import i18n from './i18n';
import { VIDEO_PLAY_EVENT, VIDEO_CLOSE_EVENT } from '../VideoPlayer.vue';

const CustomButton = () => import('../CustomButton.vue');
const IconPlay = () => import('../../icons/play.svg');
const IconClose = () => import('../../icons/close.svg');
const ResponsiveImage = () => import('../ResponsiveImage.vue');

export const YOUTUBE_PLAY_CLICK_EVENT = 'youtube-play-click';

const YouTubePlayerProps = Vue.extend({
  props: {
    autoplay: Boolean,
    youTubeId: String,
    withOverlay: {
      type: Boolean,
      default: false,
    },
    posterImgJson: String,
    videoLength: String,
    isParentExpandedMedia: Boolean,
  },
});

@Component({
  components: {
    CustomButton,
    IconPlay,
    ResponsiveImage,
    IconClose,
  },
  i18n,
})
export default class YouTubePlayer extends mixins(YouTubePlayerProps) {
  $refs!: {
    iframe: HTMLElement;
  }

  youtubePlayer: YT.Player | undefined;
  ytApiLoaded = false;
  showPlayer = !this.withOverlay;
  cookieBanner: CookieBanner | null = document.querySelector('cmm-cookie-banner');
  youTubeVideoConsent = false;
  YTPlayerInterval = 0;
  YTPlayerIntervalCounter = 0;

  mounted () {
    window.addEventListener(VIDEO_PLAY_EVENT, this.handlePauseEvent);
    document.addEventListener('usercentrics-consents-updated', this.handleConsentChange);
    this.youTubeVideoConsent = this.getCookieConsent();
    if (!this.withOverlay && this.youTubeVideoConsent) {
      this.loadIframeApi(() => {
        this.ytApiLoaded = true;
        this.onYoutubeIframeApiReady();
        this.play();
      });
    } else if (!this.withOverlay) {
      this.managePrivacyShield();
    }
  }

  handleConsentChange () {
    this.getCookieConsent();
    if (!this.showPlayer) {
      return;
    }
    this.managePrivacyShield().then(() => {
      this.$refs.iframe.innerHTML = '';
      this.loadIframeApi(() => {
        this.ytApiLoaded = true;
        this.onYoutubeIframeApiReady();
        this.YTPlayerInterval = setInterval(this.checkForAutoplay, 250);
      });
    });
  }

  checkForAutoplay () {
    if (this.youtubePlayer && this.youtubePlayer.pauseVideo) {
      this.play();
      clearInterval(this.YTPlayerInterval);
    }

    if (this.YTPlayerIntervalCounter > 9) {
      clearInterval(this.YTPlayerInterval);
    }

    this.YTPlayerIntervalCounter += 1;
  }

  async managePrivacyShield () {
    const source = `https://www.youtube-nocookie.com/embed/${this.youTubeId}`;
    this.$nextTick(async () => {
      let width = this.$refs.iframe?.clientWidth;
      let height: number | string = this.$refs.iframe?.clientHeight;
      if (height === 0) {
        height = this.$el.clientHeight;
      }
      if (this.isParentExpandedMedia) {
        width = window.innerWidth;
        height = window.innerHeight;
      }
      await this.cookieBanner?.togglePrivacyShield(source, this.$refs.iframe, height, width);
    });
  }

  loadIframeApi (callback: Function) {
    if (this.ytApiLoaded) {
      return;
    }

    if (this.getYT()) {
      // eslint-disable-next-line no-console
      console.warn('WARNING: YouTube Iframe API was already loaded by some external script');
      callback();
    } else {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      (window as any).onYouTubeIframeAPIReady = () => callback();
    }
  }

  onYoutubeIframeApiReady () {
    const YT = this.getYT();
    this.$nextTick(() => {
      this.youtubePlayer = new YT.Player(this.$refs.iframe, {
        host: 'https://www.youtube-nocookie.com',
        videoId: this.youTubeId,
        playerVars: {
          autoplay: 0,
          rel: 0,
          showinfo: 0,
          color: 'white',
          playsinline: 1,
        },
        events: {
          onReady: this.onReady,
          onStateChange: this.onStateChange,
        },
      });
      this.play();
      if (this.youtubePlayer) {
        VideoPauseHandler.getInstance().addNewVideo(this.youtubePlayer);
      }
    });
  }

  getYT (): any {
    const global: any = window;
    return global.YT;
  }

  play () {
    if (this.youtubePlayer && this.ytApiLoaded && this.youtubePlayer.pauseVideo) {
      this.youtubePlayer.playVideo();
    }
  }

  pause () {
    if (this.youtubePlayer && this.ytApiLoaded && this.youtubePlayer.pauseVideo) {
      this.youtubePlayer.pauseVideo();
    }
  }

  destroy () {
    if (this.youtubePlayer && this.ytApiLoaded) {
      this.youtubePlayer.stopVideo();
      this.youtubePlayer.destroy();
    }
  }

  handlePauseEvent (event: any) {
    if (this.youtubePlayer && this.ytApiLoaded && this.youtubePlayer.pauseVideo && this.$refs.iframe !== event.detail) {
      this.youtubePlayer.pauseVideo();
    }
  }

  triggerPlayEvent () {
    window.dispatchEvent(new CustomEvent<HTMLElement>(VIDEO_PLAY_EVENT, {
      detail: this.$refs.iframe,
    }));
  }

  dispatchButtonClickEvent (event: string) {
    this.$el.dispatchEvent(new CustomEvent<HTMLElement>(event, {
      bubbles: true,
      composed: true,
      detail: this.$refs.iframe,
    }));
  }

  onReady (event: YT.PlayerEvent) {
    this.dispatchButtonClickEvent(YOUTUBE_PLAY_CLICK_EVENT);
    const ytPlayer = event.target;
    if (this.youTubeVideoConsent) {
      // autoplay video
      if (this.autoplay) {
        ytPlayer.playVideo();
      }
    } else {
      this.managePrivacyShield();
    }
  }

  onStateChange (event: YT.OnStateChangeEvent) {
    if (event.data === this.getYT().PlayerState.PLAYING) {
      this.triggerPlayEvent();
    }
  }

  clickYouTubeButton () {
    this.youTubeVideoConsent = this.getCookieConsent();
    this.showPlayer = true;
    if (this.ytApiLoaded) {
      this.onYoutubeIframeApiReady();
    } else if (!this.youTubeVideoConsent) {
      this.$nextTick(() => {
        this.managePrivacyShield();
      });
    } else if (this.youTubeVideoConsent) {
      this.$nextTick(() => {
        if (!this.$refs.iframe.id) {
          this.$refs.iframe.innerHTML = '';
        }
        this.loadIframeApi(() => {
          this.ytApiLoaded = true;
          this.onYoutubeIframeApiReady();
          this.play();
        });
      });
    }
  }

  clickClose () {
    this.dispatchButtonClickEvent(VIDEO_CLOSE_EVENT);
    this.destroy();
    this.showPlayer = false;
  }

  getCookieConsent () {
    const cmpConsent = localStorage.getItem('cmp_consents');
    if (cmpConsent) {
      const cmpConsentJson = JSON.parse(cmpConsent);
      return cmpConsentJson.YouTube_Video;
    }
    return false;
  }
}
